/**
 * Auth Callback Component
 * Handles the redirect callback from Azure AD after SSO authentication
 */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useAuth } from '@/contexts/AuthContext';
import { Box, CircularProgress, Container, Typography } from '@mui/material';

const AuthCallback: React.FC = () => {
  const { instance } = useMsal();
  const { loginWithSSO } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleRedirect = async () => {
      try {
        // Handle the redirect response
        const response = await instance.handleRedirectPromise();
        
        if (response && response.idToken) {
          // Send ID token to backend for validation
          await loginWithSSO(response.idToken);
          // Redirect to dashboard
          navigate('/', { replace: true });
        } else {
          // No token received, redirect to login
          navigate('/login', { replace: true });
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/login', { replace: true, state: { error: 'Authentication failed' } });
      }
    };

    handleRedirect();
  }, [instance, loginWithSSO, navigate]);

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" sx={{ mt: 3 }}>
          Completing sign in...
        </Typography>
      </Box>
    </Container>
  );
};

export default AuthCallback;
